import axios from "axios";

const API_KEY = process.env.REACT_APP_API_KEY;
const BASE_URL = "https://api.tmdb.org/3";

const makeRequest = async (endpoint, params = {}) => {
  try {
    const response = await axios.get(`${BASE_URL}${endpoint}`, {
      params: {
        api_key: API_KEY,
        language: "en-US",
        ...params,
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching ${endpoint}:`, error);
    throw error;
  }
};

// Special request handler without language parameter for images
const makeImageRequest = async (endpoint, params = {}) => {
  try {
    const response = await axios.get(`${BASE_URL}${endpoint}`, {
      params: {
        api_key: API_KEY,
        ...params,
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching ${endpoint}:`, error);
    throw error;
  }
};

const apiService = {
  // Movie Methods
  getNowPlaying:()=> makeRequest("/movie/now_playing", { page: 1 }).then(data => data.results),
  getPopularMovies: () => makeRequest("/movie/popular", { page: 1 }).then(data => data.results),
  getTrendingMovies: () => makeRequest("/trending/movie/day").then(data => data.results),
  getTopRatedMovies: () => makeRequest("/movie/top_rated", { page: 1 }).then(data => data.results),
  getMovieDetails: (movieId) => makeRequest(`/movie/${movieId}`),
  getMovieCredits: (movieId) => makeRequest(`/movie/${movieId}/credits`),
  getMovieAlternativeTitles: (movieId) => makeRequest(`/movie/${movieId}/alternative_titles`).then(data => data.titles),
  getMovieWatchProviders: (movieId) => makeRequest(`/movie/${movieId}/watch/providers`).then(data => data.results.IN),
  getMovieVideos: (movieId) => makeRequest(`/movie/${movieId}/videos`).then(data => data.results),
  getMovieSimilar: (movieId) => makeRequest(`/movie/${movieId}/similar`, { page: 1 }).then(data => data.results),
  getMovieRecommendations: (movieId) => makeRequest(`/movie/${movieId}/recommendations`, { page: 1 }).then(data => data.results),
  getCollection: (collectionId) => makeRequest(`/collection/${collectionId}`).then(data => data.parts),
  getMovieImages: (movieId) => makeImageRequest(`/movie/${movieId}/images`),
  getMovieGenres: () => makeRequest("/genre/movie/list").then(data => data.genres),
  getMoviesByFilter: (page, sortBy, genres = "") => makeRequest("/discover/movie", {
    include_adult: false,
    include_video: false,
    page,
    sort_by: sortBy,
    with_genres: genres,
  }).then(data => ({ results: data.results, total_pages: data.total_pages })),

  // TV Methods
  getTrendingTV: () => makeRequest("/trending/tv/day").then(data => data.results),
  getTopRatedTV: () => makeRequest("/tv/top_rated", { page: 1 }).then(data => data.results),
  getTVAiringToday: () => makeRequest("/tv/airing_today", { page: 1 }).then(data => data.results),
  getTVDetails: (tvId) => makeRequest(`/tv/${tvId}`),
  getTVAggregateCredits: (tvId) => makeRequest(`/tv/${tvId}/aggregate_credits`),
  getTVWatchProviders: (tvId, season = 1) => makeRequest(`/tv/${tvId}/season/${season}/watch/providers`).then(data => data.results.IN),
  getTVAlternativeTitles: (tvId) => makeRequest(`/tv/${tvId}/alternative_titles`).then(data => data.results),
  getTVSeason: (tvId, seasonNumber) => makeRequest(`/tv/${tvId}/season/${seasonNumber}`),
  getEpisodeImages: (tvId, seasonNumber, episodeNumber) => makeImageRequest(`/tv/${tvId}/season/${seasonNumber}/episode/${episodeNumber}/images`).then(data => data.stills),
  getEpisodeVideos: (tvId, seasonNumber, episodeNumber) => makeRequest(`/tv/${tvId}/season/${seasonNumber}/episode/${episodeNumber}/videos`).then(data => data.results),
  getTVRecommendations: (tvId) => makeRequest(`/tv/${tvId}/recommendations`, { page: 1 }).then(data => data.results),
  getTVContentRatings: (tvId) => makeRequest(`/tv/${tvId}/content_ratings`).then(data => data.results),
  getTVSimilar: (tvId) => makeRequest(`/tv/${tvId}/similar`, { page: 1 }).then(data => data.results),
  getTVImages: (tvId) => makeImageRequest(`/tv/${tvId}/images`),
  getTVGenres: () => makeRequest("/genre/tv/list").then(data => data.genres),
  getTVByFilter: (page, sortBy, genres = "") => makeRequest("/discover/tv", {
    include_adult: false,
    include_null_first_air_dates: false,
    page,
    sort_by: sortBy,
    with_genres: genres,
  }).then(data => ({ results: data.results, total_pages: data.total_pages })),

  // Combined Methods
  getTrendingAll: () => makeRequest("/trending/all/day").then(data => data.results),
  getTrendingPeople: () => makeRequest("/person/popular").then(data => data.results),
  getTopIndianMovies: (year, month, date) => makeRequest("/discover/movie", {
    include_adult: false,
    include_video: false,
    page: 1,
    "release_date.lte": `${year}-${month < 10 ? `0${month}` : month}-${date}`,
    sort_by: "popularity.desc",
    with_origin_country: "IN",
  }).then(data => data.results),
  getTopIndianTV: (year, month, date) => makeRequest("/discover/tv", {
    "air_date.lte": `${year}-${month < 10 ? `0${month}` : month}-${date}`,
    include_adult: false,
    include_null_first_air_dates: false,
    page: 1,
    sort_by: "popularity.desc",
    with_origin_country: "IN",
  }).then(data => data.results),

  // Search Method
  searchContent: (query, type = "multi", page = 1) => makeRequest(`/search/${type}`, { query, page }).then(data => data),

  // People Methods
  getPersonDetails: (personId) => makeRequest(`/person/${personId}`),
  getPersonCombinedCredits: (personId) => makeRequest(`/person/${personId}/combined_credits`),
  getPersonExternalIds: (personId) => makeRequest(`/person/${personId}/external_ids`),
  getPersonImages: (personId) => makeImageRequest(`/person/${personId}/images`).then(data => data.profiles),
  getPersonMovieCredits: (personId) => makeRequest(`/person/${personId}/movie_credits`),
};

export default apiService;